/**
 * Units for the Transport settings page (M38-T07).
 *
 * Money is integer pesewas everywhere (CLAUDE.md); staff type cedis. The
 * conversion works on the TEXT, never through a float: 0.29 * 100 is
 * 28.999999999999996, and a fare must not lose a pesewa on the way in.
 *
 * Notice and the assignment cut-off are stored in minutes and shown in hours,
 * because that is how the desk thinks about them ("12 hours before pickup").
 */

/** GHS 100,000: the server's typo guard, checked here too so the page can say
 * so in words. */
export const MAX_FARE_PESEWAS = 10_000_000

export type GhsParse = { ok: true; pesewas: number | null } | { ok: false }

const GHS = /^(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?$/

/** "12.50" → 1250 pesewas; "" → null ("not set"). Anything else is refused. */
export function parseGhs(text: string): GhsParse {
  const trimmed = text.trim()
  if (trimmed === '') return { ok: true, pesewas: null }
  const match = GHS.exec(trimmed)
  if (!match) return { ok: false }
  const cedis = Number((match[1] ?? '').replace(/,/g, ''))
  const pesewas = Number((match[2] ?? '').padEnd(2, '0'))
  return { ok: true, pesewas: cedis * 100 + pesewas }
}

/** The text a fare input starts with: "12.50", or "" when not set. */
export function ghsInput(pesewas: number | null): string {
  if (pesewas === null) return ''
  const cedis = Math.floor(pesewas / 100)
  const rest = String(pesewas % 100).padStart(2, '0')
  return `${cedis}.${rest}`
}

export type SettingCopy = {
  label: string
  /** The unit the page shows and the staff member types. */
  unit: string
  /** Minutes stored, hours shown. */
  inHours?: boolean
  hint?: string
}

/** No service may be booked closer to pickup than the cut-off (owner ruling,
 * 2026-11-03); the server refuses either way round, and says why. */
const NOTICE_HINT = 'At least the assignment cut-off.'

/** Every staff-editable setting (M38-T06's ranges are the server's guard). The
 * minimum unaccompanied age is not here: the owner fixed it at 16 (Q13). */
export const SETTING_COPY: Record<string, SettingCopy> = {
  min_notice_minutes_standard_ride: {
    label: 'Minimum notice — Standard Ride',
    unit: 'hours',
    inHours: true,
    hint: NOTICE_HINT,
  },
  min_notice_minutes_pts: {
    label: 'Minimum notice — Patient Transport',
    unit: 'hours',
    inHours: true,
    hint: NOTICE_HINT,
  },
  min_notice_minutes_car_hire: {
    label: 'Minimum notice — Car Hire with Driver',
    unit: 'hours',
    inHours: true,
    hint: NOTICE_HINT,
  },
  assignment_cutoff_minutes: {
    label: 'Assignment cut-off — hours before pickup',
    unit: 'hours',
    inHours: true,
    hint: 'A booking with no driver by then turns red in the queue. No longer than the shortest minimum notice.',
  },
  checkout_hold_minutes: {
    label: 'Place held while the customer pays',
    unit: 'minutes',
  },
  max_days_ahead: { label: 'Bookings open up to', unit: 'days ahead' },
  pickup_early_arrival_minutes: {
    label: 'Arrive before the appointment',
    unit: 'minutes',
  },
  pickup_change_window_minutes: {
    label: 'Customer may move the pickup by up to',
    unit: 'minutes',
  },
  stretcher_capacity: {
    label: 'Stretcher vehicles available',
    unit: 'vehicles',
    hint: '0 means none: stretcher trips are booked by phone.',
  },
  caregiver_seats: { label: 'Free caregiver seats', unit: 'seats' },
}

/** A stored value as the page shows it. */
export function settingInput(key: string, value: number): string {
  if (!SETTING_COPY[key]?.inHours) return String(value)
  // Two decimals at most: 20 minutes reads "0.33", not "0.3333333333333333".
  return String(Number((value / 60).toFixed(2)))
}

/** What the staff member typed, in the stored unit, or null if it is not a
 * whole number of that unit (the page says so; the server checks the range). */
export function fromSettingInput(key: string, text: string): number | null {
  const trimmed = text.trim()
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null
  const typed = Number(trimmed)
  const stored = SETTING_COPY[key]?.inHours ? typed * 60 : typed
  // Hours to two decimals can still land between minutes (1.01 h = 60.6 min).
  const rounded = Math.round(stored)
  return Math.abs(stored - rounded) < 1e-9 ? rounded : null
}
